import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react'
import { formatDistanceToNowStrict } from 'date-fns'
import { BadgeCheck, Box, ChartLine, Code, Database, FileCode, Globe, LockOpen, Notebook, Search, TriangleAlert, Zap, type LucideIcon } from 'lucide-react'

import { Button } from '../../ui/Button'
import { DialogContent } from '../../ui/Dialog'
import { Input } from '../../ui/Field'
import { CATEGORY_LABELS, PLAINTEXT_TILE_NOTE, POPULAR_IMAGES, POPULAR_IMAGE_CATEGORIES, STATEFUL_NOTE, presetVersion, type PopularImage, type PopularImageCategory } from '../../core/popularImages'
import { TEMPLATE_CATEGORIES, TEMPLATE_CATEGORY_LABELS, TEMPLATE_SUMMARIES, searchApps, type AppResult, type TemplateCategory, type TemplateSummary } from '../../core/templateCatalog'

export const PRESET_ICONS: Record<PopularImage['icon'], LucideIcon> = { globe: Globe, database: Database, zap: Zap, notebook: Notebook, box: Box, chart: ChartLine, code: Code, search: Search }

/** Where a tile renders; it keeps ids unique while the dialog repeats a card tile. */
type Scope = 'card' | 'dialog'

/** Stateful entries say so, in the tile and after they are applied (see `statefulNote`). */
export { STATEFUL_NOTE }

/** "3 apps", said once typing pauses; filtering itself is immediate. */
export function useSettledAnnouncement(text: string, delay = 300): string {
  const [announced, setAnnounced] = useState('')
  useEffect(() => {
    const timer = window.setTimeout(() => setAnnounced(text), delay)
    return () => window.clearTimeout(timer)
  }, [text, delay])
  return announced
}

export function countLabel(count: number): string {
  return count === 0 ? 'No apps match' : `${count} ${count === 1 ? 'app' : 'apps'}`
}

/**
 * The search box above the tiles, in the card and in the dialog. Down arrow
 * moves to the first result (the tiles stay a group of buttons, not a
 * combobox); Escape clears a non-empty search; Enter never submits the form
 * the card sits in.
 */
export function AppSearchField({ id, value, onChange, inputRef, resultsRef }: { id: string; value: string; onChange: (value: string) => void; inputRef: RefObject<HTMLInputElement>; resultsRef: RefObject<HTMLElement> }) {
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') event.preventDefault()
    if (event.key === 'Escape' && value !== '') {
      event.preventDefault()
      onChange('')
    }
    if (event.key === 'ArrowDown') {
      const first = resultsRef.current?.querySelector<HTMLElement>('[data-preset]:not([data-preset="own"])')
      if (first) {
        event.preventDefault()
        first.focus()
      }
    }
  }
  return <Input ref={inputRef} id={id} type="search" label="Search apps" hideLabel className="app-search" placeholder={'Search apps — try "postgres", "database"'} spellCheck={false} autoCapitalize="off" autoComplete="off" value={value} onChange={(event) => onChange(event.target.value)} onKeyDown={onKeyDown} />
}

function PublisherLine({ publisher }: { publisher: PopularImage['publisher'] }) {
  if (publisher.kind === 'project') return <span className="image-preset-meta">{publisher.name}</span>
  return <span className="image-preset-meta">
    <BadgeCheck aria-hidden="true" size={14} className="image-preset-badge" />
    {publisher.kind === 'docker-official' ? 'Official image' : `Verified publisher · ${publisher.name}`}
  </span>
}

function FreshnessLine({ preset }: { preset: PopularImage }) {
  // A clock behind the registry's would read "updated in 3 days"; say "0 seconds ago" instead.
  const updated = preset.upstreamUpdatedAt === null ? null : new Date(Math.min(Date.parse(preset.upstreamUpdatedAt), Date.now()))
  const title = [updated && `Tag pushed ${preset.upstreamUpdatedAt!.slice(0, 10)}`, `checked ${preset.verifiedAt.slice(0, 10)}`].filter(Boolean).join(' · ')
  return <span className="image-preset-meta" title={title}>
    <span className="image-preset-version">{presetVersion(preset)}</span>
    {updated && ` · updated ${formatDistanceToNowStrict(updated, { addSuffix: true })}`}
  </span>
}

export function PresetTile({ scope, preset, pressed, onClick }: { scope: Scope; preset: PopularImage; pressed: boolean; onClick: () => void }) {
  const [logoFailed, setLogoFailed] = useState(false)
  const base = `image-preset-${scope}-${preset.id}`
  const Icon = PRESET_ICONS[preset.icon]
  const unencrypted = (preset.plaintextPorts?.length ?? 0) > 0
  const describedBy = [`${base}-description`, preset.stateful && `${base}-stateful`, unencrypted && `${base}-plaintext`].filter(Boolean).join(' ')
  return <Button type="button" variant="secondary" className="image-preset" data-preset={preset.id} aria-pressed={pressed} aria-label={preset.name} aria-describedby={describedBy} onClick={onClick}>
    <span className="image-preset-mark" aria-hidden="true">
      {preset.logo !== null && !logoFailed
        ? <img className="image-preset-logo" src={`${import.meta.env.BASE_URL}logos/${preset.logo}`} alt="" width={24} height={24} loading="lazy" onError={() => setLogoFailed(true)} />
        : <Icon size={20} className="image-preset-icon" />}
    </span>
    <span className="image-preset-text">
      <span className="image-preset-name">
        {preset.name}
        {/* A caution only where data can be lost: a volume keeps it across restarts and updates. */}
        {preset.stateful && !preset.volume && <TriangleAlert aria-hidden="true" size={14} className="image-preset-stateful" />}
      </span>
      {preset.stateful && <span className="sr-only" id={`${base}-stateful`}>{preset.volume ? 'Stores data on a persistent volume.' : 'Stores data on the instance disk.'}</span>}
      <PublisherLine publisher={preset.publisher} />
      <FreshnessLine preset={preset} />
      {unencrypted && <span className="image-preset-meta image-preset-plaintext" id={`${base}-plaintext`}>
        <LockOpen aria-hidden="true" size={14} className="image-preset-badge" />{PLAINTEXT_TILE_NOTE}
      </span>}
      <span className="image-preset-description" id={`${base}-description`}>{preset.description}</span>
    </span>
  </Button>
}

/** Said on every template tile: choosing one leaves this form. */
export const TEMPLATE_TILE_NOTE = 'Opens in the SDL editor'

/**
 * A template among the apps. It is not a preset: it applies nothing to the
 * draft, it opens the SDL editor on the template's questions, so the tile is
 * a plain button rather than a toggle.
 */
export function TemplateTile({ scope, template, onClick }: { scope: Scope; template: TemplateSummary; onClick: () => void }) {
  const [logoFailed, setLogoFailed] = useState(false)
  const base = `template-tile-${scope}-${template.id}`
  const unencrypted = (template.unencryptedPorts?.length ?? 0) > 0
  const describedBy = [`${base}-note`, `${base}-description`, unencrypted && `${base}-plaintext`].filter(Boolean).join(' ')
  return <Button type="button" variant="secondary" className="image-preset" data-preset={`template-${template.id}`} aria-label={template.name} aria-describedby={describedBy} onClick={onClick}>
    <span className="image-preset-mark" aria-hidden="true">
      {template.logo !== null && !logoFailed
        ? <img className="image-preset-logo" src={`${import.meta.env.BASE_URL}logos/${template.logo}`} alt="" width={24} height={24} loading="lazy" onError={() => setLogoFailed(true)} />
        : <FileCode size={20} className="image-preset-icon" />}
    </span>
    <span className="image-preset-text">
      <span className="image-preset-name">{template.name}</span>
      <span className="image-preset-meta" id={`${base}-note`}><FileCode aria-hidden="true" size={14} className="image-preset-badge" />{TEMPLATE_TILE_NOTE}</span>
      {unencrypted && <span className="image-preset-meta image-preset-plaintext" id={`${base}-plaintext`}>
        <LockOpen aria-hidden="true" size={14} className="image-preset-badge" />{PLAINTEXT_TILE_NOTE}
      </span>}
      <span className="image-preset-description" id={`${base}-description`}>{template.description}</span>
    </span>
  </Button>
}

/** One search result, whichever kind it is. */
export function AppResultTile({ scope, result, selectedId, onChoosePreset, onChooseTemplate }: { scope: Scope; result: AppResult; selectedId: string | undefined; onChoosePreset: (preset: PopularImage) => void; onChooseTemplate: (template: TemplateSummary) => void }) {
  return result.kind === 'image'
    ? <PresetTile scope={scope} preset={result.preset} pressed={selectedId === result.preset.id} onClick={() => onChoosePreset(result.preset)} />
    : <TemplateTile scope={scope} template={result.template} onClick={() => onChooseTemplate(result.template)} />
}

type AppCategory = PopularImageCategory | TemplateCategory

const APP_CATEGORIES: readonly AppCategory[] = [...POPULAR_IMAGE_CATEGORIES, ...TEMPLATE_CATEGORIES.filter((key) => !(POPULAR_IMAGE_CATEGORIES as readonly string[]).includes(key))]
const APP_CATEGORY_LABELS: Record<AppCategory, string> = { ...CATEGORY_LABELS, ...TEMPLATE_CATEGORY_LABELS }

function categoryOf(result: AppResult): AppCategory {
  return result.kind === 'image' ? result.preset.category : result.template.category
}

function resultKey(result: AppResult): string {
  return result.kind === 'image' ? result.preset.id : `template-${result.template.id}`
}

/** Everything "Browse all" counts: every app and every template. */
export const APP_COUNT = POPULAR_IMAGES.length + TEMPLATE_SUMMARIES.length

export function OwnImageTile({ pressed, onClick }: { pressed: boolean; onClick: () => void }) {
  return <Button type="button" variant="secondary" className="image-preset" data-preset="own" aria-pressed={pressed} aria-label="Your own image" aria-describedby="image-preset-own-description" onClick={onClick}>
    <span className="image-preset-mark" aria-hidden="true"><Box size={20} className="image-preset-icon" /></span>
    <span className="image-preset-text">
      <span className="image-preset-name">Your own image</span>
      <span className="image-preset-description" id="image-preset-own-description">Any public registry</span>
    </span>
  </Button>
}

/**
 * Every entry, with the same search and a category filter. The body mounts
 * each time the dialog opens, so it starts from the card's query and "All".
 */
export function BrowseAllDialogContent({ initialQuery, selectedId, onChoose, onChooseTemplate, returnFocus }: { initialQuery: string; selectedId: string | undefined; onChoose: (preset: PopularImage) => void; onChooseTemplate: (template: TemplateSummary) => void; returnFocus: () => HTMLElement | null }) {
  const [query, setQuery] = useState(initialQuery)
  const [category, setCategory] = useState<AppCategory | 'all'>('all')
  const searchRef = useRef<HTMLInputElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const everything: AppResult[] = [...POPULAR_IMAGES.map((preset): AppResult => ({ kind: 'image', preset })), ...TEMPLATE_SUMMARIES.map((template): AppResult => ({ kind: 'template', template }))]
  const matching = query.trim() === '' ? everything : searchApps(query)
  const shown = category === 'all' ? matching : matching.filter((result) => categoryOf(result) === category)
  const categories = APP_CATEGORIES.filter((key) => everything.some((result) => categoryOf(result) === key))
  const announcement = useSettledAnnouncement(countLabel(shown.length))

  // An Escape the search field used to clear itself does not also close the dialog.
  const onEscapeKeyDown = (event: globalThis.KeyboardEvent) => {
    if (event.target === searchRef.current && query !== '') event.preventDefault()
  }

  return <DialogContent title="Apps" description="Every starting point, each pinned and checked. Choosing one fills in the form; you can change anything after." wide onEscapeKeyDown={onEscapeKeyDown} returnFocus={returnFocus}>
    <AppSearchField id="app-search-dialog" value={query} onChange={setQuery} inputRef={searchRef} resultsRef={gridRef} />
    <div className="app-categories" role="group" aria-label="Category">
      <Button type="button" variant="secondary" size="sm" className="app-category" aria-pressed={category === 'all'} onClick={() => setCategory('all')}>All <span className="app-category-count">{matching.length}</span></Button>
      {categories.map((key) => <Button key={key} type="button" variant="secondary" size="sm" className="app-category" aria-pressed={category === key} onClick={() => setCategory(key)}>
        {APP_CATEGORY_LABELS[key]} <span className="app-category-count">{matching.filter((result) => categoryOf(result) === key).length}</span>
      </Button>)}
    </div>
    <span className="sr-only" aria-live="polite" aria-atomic="true">{announcement}</span>
    <div className="image-presets image-presets--dialog" role="group" aria-label="Apps" ref={gridRef}>
      {shown.map((result) => <AppResultTile key={resultKey(result)} scope="dialog" result={result} selectedId={selectedId} onChoosePreset={onChoose} onChooseTemplate={onChooseTemplate} />)}
    </div>
    {shown.length === 0 && <p className="app-search-empty">No app matches{query.trim() ? ` "${query.trim()}"` : ''}{category === 'all' ? '' : ` in ${APP_CATEGORY_LABELS[category]}`}.</p>}
  </DialogContent>
}
